import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { api } from '../lib/api';
import { timeAgo } from '../lib/format';
import { cx } from './ui';

interface Note { id: number; type: string; title: string; body: string | null; link: string | null; readAt: string | null; createdAt: string }

/** Bell with unread count; opens a short list. Clicking an item marks it read and opens its link. */
export function NotificationBell({ align = 'left' }: { align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: Note[]; unread: number }>('/notifications'),
    refetchInterval: 45_000,
  });
  const markRead = useMutation({
    mutationFn: (ids?: number[]) => api.post('/notifications/read', ids ? { ids } : {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const unread = q.data?.unread ?? 0;

  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} aria-expanded={open}
        className="relative rounded-lg p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">
        <Bell className="size-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex min-w-4.5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className={cx('absolute z-40 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900', align === 'right' ? 'right-0' : 'left-0')}>
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5 dark:border-slate-800">
              <span className="font-semibold">Notifications</span>
              {unread > 0 && <button className="text-sm text-brand-700 hover:underline" onClick={() => markRead.mutate(undefined)}>Mark all read</button>}
            </div>
            <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
              {(q.data?.items ?? []).length === 0 && <li className="px-4 py-8 text-center text-sm text-slate-500">You’re all caught up.</li>}
              {q.data?.items.map((n) => (
                <li key={n.id}>
                  <button
                    className={cx('block w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800', !n.readAt && 'bg-brand-50/50')}
                    onClick={() => {
                      if (!n.readAt) markRead.mutate([n.id]);
                      setOpen(false);
                      if (n.link) navigate(n.link);
                    }}>
                    <p className={cx('text-sm', !n.readAt && 'font-semibold')}>{n.title}</p>
                    {n.body && <p className="line-clamp-2 text-sm text-slate-600 dark:text-slate-400">{n.body}</p>}
                    <p className="mt-0.5 text-xs text-slate-500">{timeAgo(n.createdAt)}</p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
